import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { roleHome } from "@/lib/roles";
import Logo from "@/components/marketplace/Logo";
import Seo from "@/components/Seo";

export default function AuthVerify() {
  const { verifyLink } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [err, setErr] = useState("");
  const [noAccount, setNoAccount] = useState(false); // login link for an unknown email
  const ran = useRef(false); // magic tokens are single-use — never verify twice

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const token = sp.get("token");
    if (!token) { setErr("Missing sign-in token."); return; }
    verifyLink(token)
      .then((user) => nav(roleHome(user)))
      // A sign-in link for an email with no account resolves here (existence is
      // never revealed at request time), so 404 means "register", not "retry".
      .catch((ex) => (ex?.response?.status === 404 ? setNoAccount(true) : setErr(apiErr(ex))));
  }, [sp, verifyLink, nav]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6">
      <Seo title="Signing you in" noindex />
      <div className="text-center max-w-md">
        <Logo className="h-11 w-11 mx-auto mb-4" />
        {noAccount ? (
          <>
            <h1 className="font-serif text-3xl mb-3">No account found</h1>
            <p className="text-sm text-muted-foreground mb-6">There's no account for that email yet. Create one to choose whether you're a Client or a Designer.</p>
            <Link to="/register" data-testid="verify-register" className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Create an account
            </Link>
          </>
        ) : err ? (
          <>
            <h1 className="font-serif text-3xl mb-3">Sign-in failed</h1>
            <p className="text-sm text-muted-foreground mb-6">{err}</p>
            <Link to="/login" className="inline-flex items-center gap-2 border border-primary text-primary px-6 py-3 text-xs uppercase tracking-widest hover:bg-primary hover:text-primary-foreground transition-colors duration-300">
              Request a new link
            </Link>
          </>
        ) : (
          <p className="text-muted-foreground text-sm tracking-widest uppercase">Signing you in…</p>
        )}
      </div>
    </div>
  );
}
